import { json, redirect } from '@remix-run/node';
import { useLoaderData, Link } from '@remix-run/react';
import { useState, useEffect } from 'react';
import type { LoaderFunctionArgs, MetaFunction } from '@remix-run/node';
import { motion } from 'framer-motion';
import { Badge } from '~/components/ui/badge';
import { AuthorInfo } from '~/components/blog/AuthorInfo';
import { TableOfContents } from '~/components/blog/TableOfContents';
import { TagList } from '~/components/blog/TagList';
import { MDXContent } from '~/components/mdx/MDXContent';
import { getPost, getAllPosts } from '~/lib/blog.static';
import { buildMeta } from '~/lib/seo';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { Display } from '~/components/ui/typography';
import { Container } from '~/components/ui/container';
import { staggerContainer, staggerItem, fadeUp, scaleFade } from '~/lib/animations';
import { useAnimationState } from '~/lib/motion-utils';

export async function loader({ params }: LoaderFunctionArgs) {
  const { slug } = params;

  if (!slug) {
    throw redirect('/blog');
  }

  const compiledPost = await getPost(slug);

  if (!compiledPost) {
    throw new Response('Post not found', { status: 404 });
  }

  // Flatten the post structure to match BlogPost interface
  const post = {
    slug: compiledPost.slug,
    title: compiledPost.frontmatter.title,
    description: compiledPost.frontmatter.description,
    excerpt: compiledPost.frontmatter.description,
    date: compiledPost.frontmatter.date,
    author: compiledPost.frontmatter.author,
    category: compiledPost.frontmatter.category,
    tags: compiledPost.frontmatter.tags || [],
    image: compiledPost.frontmatter.image,
    readingTime: compiledPost.frontmatter.readingTime,
    featured: compiledPost.frontmatter.featured,
    code: compiledPost.code,
    headings: compiledPost.headings || []
  };

  // Get next and previous posts for navigation
  // Note: In a real app, you'd want to get these based on date ordering
  const sortedPosts = await getAllPosts();
  const currentIndex = sortedPosts.findIndex(p => p.slug === slug);
  const prevPost = currentIndex > 0 ? sortedPosts[currentIndex - 1] : null;
  const nextPost = currentIndex < sortedPosts.length - 1 ? sortedPosts[currentIndex + 1] : null;

  return json({
    post,
    prevPost,
    nextPost,
  });
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  if (!data?.post) {
    return buildMeta({
      title: 'Post Not Found - Lumora',
      description: 'The requested blog post could not be found.',
    });
  }

  return buildMeta({
    title: `${data.post.title} - Lumora Blog`,
    description: data.post.description,
    image: data.post.image,
    article: {
      publishedTime: data.post.date,
      author: data.post.author,
      tags: data.post.tags,
    },
  });
};

export default function BlogPostPage() {
  const { post, prevPost, nextPost } = useLoaderData<typeof loader>();
  const { getVariants } = useAnimationState();
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let rafId: number;
    let ticking = false;

    const updateProgress = () => {
      const scrolled = window.scrollY;
      const total = document.documentElement.scrollHeight - window.innerHeight;
      const newProgress = (scrolled / total) * 100;
      setProgress(newProgress);
      ticking = false;
    };

    const handleScroll = () => {
      if (!ticking) {
        rafId = requestAnimationFrame(updateProgress);
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    // Set initial progress
    updateProgress();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, []);

  // Add IDs to headings for anchor navigation
  useEffect(() => {
    if (!post.headings || post.headings.length === 0) return;

    post.headings.forEach((heading) => {
      const headingId = heading.id || heading.slug;
      // Find heading by text content
      const headingElements = document.querySelectorAll('.prose h1, .prose h2, .prose h3, .prose h4, .prose h5, .prose h6');
      headingElements.forEach((element) => {
        if (element.textContent?.trim() === heading.text.trim() && !element.id) {
          element.id = headingId;
        }
      });
    });
  }, [post.headings]);

  return (
    <>
      {/* Reading Progress Bar */}
      <div className="fixed top-0 left-0 w-full h-1 bg-gray-200 z-50">
        <div
          className="h-full bg-accent transition-[width] duration-150 ease-out"
          style={{ width: `${progress}%` }}
        />
      </div>

      <Container size="default" padding="large" as="article" className="!pt-8 md:!pt-12 lg:!pt-16">
        {/* Hero Image */}
        {post.image && (
          <motion.div
            className="relative aspect-video rounded-lg overflow-hidden mb-8 opacity-0"
            initial="hidden"
            animate="visible"
            variants={getVariants(scaleFade)}
          >
            <img
              src={post.image}
              alt={post.title}
              className="w-full h-full object-cover"
            />
          </motion.div>
        )}

        {/* Header */}
        <motion.header
          className="max-w-4xl mx-auto mb-8 opacity-0"
          id="article-top"
          initial="hidden"
          animate="visible"
          variants={getVariants({
            ...staggerContainer,
            visible: {
              opacity: 1,
              transition: {
                staggerChildren: 0.1,
                delayChildren: 0.15,
              },
            },
          })}
        >
          <motion.div className="flex gap-2 mb-4" variants={getVariants(staggerItem)}>
            <Badge>{post.category}</Badge>
            <Badge variant="outline">{post.readingTime} min read</Badge>
          </motion.div>

          <motion.div variants={getVariants(staggerItem)}>
            <Display className="mb-4">{post.title}</Display>
          </motion.div>

          <motion.div className="text-gray-600" variants={getVariants(staggerItem)}>
            <AuthorInfo author={post.author} date={post.date} />
          </motion.div>
        </motion.header>

        {/* Content with TOC */}
        <div className="relative grid lg:grid-cols-4 gap-8">
          <motion.aside
            className="hidden lg:block lg:col-span-1 opacity-0"
            initial="hidden"
            animate="visible"
            variants={getVariants({
              ...fadeUp,
              visible: {
                ...fadeUp.visible,
                transition: {
                  ...(typeof fadeUp.visible === 'object' && 'transition' in fadeUp.visible
                    ? fadeUp.visible.transition
                    : {}),
                  delay: 0.3,
                },
              },
            })}
          >
            <div className="sticky top-24 bg-gray-900/25 rounded-lg p-4 border border-gray-800/25 max-h-[calc(100vh-7rem)] overflow-y-auto [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-gray-800 [&::-webkit-scrollbar-thumb]:bg-gray-700 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-gray-600">
              <TableOfContents headings={post.headings} />
            </div>
          </motion.aside>

          <motion.div
            className="lg:col-span-3 opacity-0"
            initial="hidden"
            animate="visible"
            variants={getVariants({
              ...fadeUp,
              visible: {
                ...fadeUp.visible,
                transition: {
                  ...(typeof fadeUp.visible === 'object' && 'transition' in fadeUp.visible
                    ? fadeUp.visible.transition
                    : {}),
                  delay: 0.35,
                },
              },
            })}
          >
            {post.code ? (
              <MDXContent
                code={post.code}
                className="prose prose-lumora max-w-none [&>*:first-child]:mt-0"
              />
            ) : (
              <div className="prose prose-lumora max-w-none">
                <p>Content is not available</p>
              </div>
            )}
          </motion.div>
        </div>

        {/* Footer */}
        <motion.footer
          className="max-w-4xl mx-auto mt-12 space-y-8 opacity-0"
          initial="hidden"
          animate="visible"
          variants={getVariants({
            ...staggerContainer,
            visible: {
              opacity: 1,
              transition: {
                staggerChildren: 0.15,
                delayChildren: 0.5,
              },
            },
          })}
        >
          <motion.div variants={getVariants(staggerItem)}>
            <TagList tags={post.tags || []} />
          </motion.div>

          {/* Navigation */}
          <motion.div
            className="flex justify-between items-center py-8 border-t"
            variants={getVariants(staggerItem)}
          >
            {prevPost ? (
              <Link
                to={`/blog/${prevPost.slug}`}
                className="flex items-center gap-2 text-gray-300 hover:text-primary transition-colors"
              >
                <ChevronLeftIcon className="w-5 h-5" />
                <div className="text-left">
                  <div className="text-sm">Previous</div>
                  <div className="font-semibold">{prevPost.title}</div>
                </div>
              </Link>
            ) : (
              <div />
            )}

            {nextPost ? (
              <Link
                to={`/blog/${nextPost.slug}`}
                className="flex items-center gap-2 text-gray-300 hover:text-primary text-right transition-colors"
              >
                <div>
                  <div className="text-sm">Next</div>
                  <div className="font-semibold">{nextPost.title}</div>
                </div>
                <ChevronRightIcon className="w-5 h-5" />
              </Link>
            ) : (
              <div />
            )}
          </motion.div>
        </motion.footer>
      </Container>
    </>
  );
}